/**
 * Floor Plan Detail tab-switch and "View floor plan" click delegation for the Native Interactive
 * Site Map listing panel.
 *
 * Event delegation over the panel root — floorPlanDetail.plugin.js's content (including the tab
 * bar and the mobile expand button) is re-rendered/torn down as the user navigates between views,
 * so the listener binds once on the stable panel container instead of being re-attached per
 * render. Pure DOM — no Redux, no CSS, no business logic. Mirrors the delegation style of
 * handlers/getNotifiedInteractionHandlers.js.
 *
 * Tab clicks are filtered to just the Floor Plan Detail view's own tab values
 * (TAB_LOT_AND_ELEVATION / TAB_FLOOR_PLAN_CONFIG) — the Available chip's Floor Plans/Quick
 * Move-Ins tabs share the same [data-tab-value] attribute but are already tracked via
 * tabSelectedOccurred$ (observed on Redux state, see plugins/tracking.plugin.js).
 *
 * @module nativeInteractiveSiteMap/handlers/floorPlanDetailInteractionHandlers
 */
import { TAB_LOT_AND_ELEVATION, TAB_FLOOR_PLAN_CONFIG } from '../constants';

const TAB_VALUE_SELECTOR = '[data-tab-value]';
const VIEW_FLOOR_PLAN_BUTTON_SELECTOR = '[data-image-expand-button]';
const TRACKED_TAB_VALUES = new Set([TAB_LOT_AND_ELEVATION, TAB_FLOOR_PLAN_CONFIG]);

/**
 * @returns {{ bind: Function, unbind: Function }}
 */
function createFloorPlanDetailInteractionHandlers() {
  const state = {
    rootElement: null,
    clickHandler: null,
  };

  function unbind() {
    if (state.rootElement && state.clickHandler) {
      state.rootElement.removeEventListener('click', state.clickHandler);
    }
    state.rootElement = null;
    state.clickHandler = null;
  }

  /**
   * @param {HTMLElement} rootElement - the stable panel container to delegate from
   * @param {object} handlers
   * @param {(tabValue: string) => void} handlers.onTabSelected - called with TAB_LOT_AND_ELEVATION
   *   or TAB_FLOOR_PLAN_CONFIG when one of this view's own tabs is clicked
   * @param {() => void} [handlers.onViewFloorPlanClicked] - called on a "View floor plan" click;
   *   omit to not bind it at all (mobile-only — see plugins/tracking.plugin.js)
   */
  function bind(rootElement, { onTabSelected, onViewFloorPlanClicked } = {}) {
    unbind();
    state.rootElement = rootElement;
    state.clickHandler = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const tabElement = event.target.closest(TAB_VALUE_SELECTOR);
      const tabValue = tabElement?.dataset.tabValue;
      if (tabValue && TRACKED_TAB_VALUES.has(tabValue) && typeof onTabSelected === 'function') {
        onTabSelected(tabValue);
      }

      if (typeof onViewFloorPlanClicked === 'function' && event.target.closest(VIEW_FLOOR_PLAN_BUTTON_SELECTOR)) {
        onViewFloorPlanClicked();
      }
    };
    rootElement.addEventListener('click', state.clickHandler);
  }

  return { bind, unbind };
}

export default createFloorPlanDetailInteractionHandlers;
